import { Auth, useAuthedRequest } from "shared/@auth/Auth";
import { useCallback, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import createNotification from "shared/@utils/createNotification";
import toast from "react-hot-toast";
import { useLogger } from "shared/@hooks/useLogger";

export const usePlugins = () => {
  const { makeAuthedRequest } = Auth.useContainer();

  const { data, isError, isLoading, refetch } = useQuery(
    ["organizations"],
    () =>
      makeAuthedRequest(`/dreamstream/organizations`, {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  const {
    data: losData,
    loading: losLoading,
    refetch: losRefetch,
  } = useQuery(["plugins"], () =>
    makeAuthedRequest(`/dreamstream/los/plugins`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        return res.json();
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  return {
    data,
    refetch,
    isError,
    isLoading,
    losData,
    losLoading,
    losRefetch,
  };
};

export const useDeletePlugins = () => {
  const { makeAuthedRequest } = Auth.useContainer();
  const { refetch, losRefetch } = usePlugins();

  const deletePlugin = useMutation(
    // Query Key for invalidating data
    ["pluginDelete"],
    async (deleteObject) =>
      makeAuthedRequest(`/dreamstream/${deleteObject.type}/plugin/delete`, {
        method: "DELETE",
        body: JSON.stringify(deleteObject.deleteData),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        losRefetch();
        refetch();
      },
    }
  );

  return {
    deletePlugin,
  };
};

export function useGroupPlugins() {
  const { logError } = useLogger({ prefix: "useGroupPlugins" });
  const [groupPlugins, setGroupPlugins] = useState([]);

  const { data, loading, error } = useAuthedRequest(
    `/dreamstream/organizations`
  );

  useEffect(() => {
    if (error) {
      logError(error);
    }
  }, [error, logError]);

  useEffect(() => {
    if (data != null) {
      setGroupPlugins(data);
    }
  }, [data]);

  return {
    groupPlugins,
    loading,
  };
}

//Create new group plugin
export function useAddGroupPlugin() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();

  const addGroupPlugin = useMutation((group) =>
    makeAuthedRequest(`/dreamstream/group/plugins`, {
      method: "POST",
      body: JSON.stringify(group),
    }).then(async (res) => {
      if (res.ok) {
        return;
      } else {
        const body = await res.json();
        createNotification({
          type: "danger",
          title: "Error",
          message: body.error,
          autoDismiss: true,
        });
        throw new Error(body.error);
      }
    })
  );

  const { mutate } = useMutation(addGroupPlugin, {
    onSuccess: () => {
      queryClient.invalidateQueries("plugins");
      createNotification({
        type: "success",
        title: "Saved",
        message: "A new group plugin was saved successfully.",
        autoDismiss: true,
      });
    },
    onError: (err) => {
      console.error(err);
      toast.error("Something went wrong!");
    },
  });

  return {
    addGroupPlugin,
    mutate,
  };
}

//Create new meta plugin
export function useAddMetaPlugin() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();

  const addMetaPlugin = useMutation((group) =>
    makeAuthedRequest(`/dreamstream/meta/plugins`, {
      method: "POST",
      body: JSON.stringify(group),
    }).then(async (res) => {
      if (res.ok) {
        return;
      } else {
        const body = await res.json();
        createNotification({
          type: "danger",
          title: "Error",
          message: body.error,
          autoDismiss: true,
        });
        throw new Error(body.error);
      }
    })
  );

  const { mutate } = useMutation(addMetaPlugin, {
    onSuccess: () => {
      queryClient.invalidateQueries("plugins");
      createNotification({
        type: "success",
        title: "Saved",
        message: "A new meta plugin was saved successfully.",
        autoDismiss: true,
      });
    },
    onError: (err) => {
      console.error(err);
      toast.error("Something went wrong!");
    },
  });

  return { mutate, addMetaPlugin };
}

//Create new LOS plugin
export function useAddLosPlugin() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();

  const addLosPlugin = useMutation((los) =>
    makeAuthedRequest(`/dreamstream/los/plugins`, {
      method: "POST",
      body: JSON.stringify(los),
    }).then(async (res) => {
      if (res.ok) {
        return;
      } else {
        const body = await res.json();
        createNotification({
          type: "danger",
          title: "Error",
          message: body.error,
          autoDismiss: true,
        });
        throw new Error(body.error);
      }
    })
  );

  const { mutate } = useMutation(addLosPlugin, {
    onSuccess: () => {
      queryClient.invalidateQueries("plugins");
      createNotification({
        type: "success",
        title: "Saved",
        message: "A new LOS plugin was saved successfully.",
        autoDismiss: true,
      });
    },
    onError: (err) => {
      console.error(err);
      toast.error("Something went wrong!");
    },
  });

  return { addLosPlugin, mutate };
}
